import { JSX } from 'react';
import {
  Stack,
  Card,
  CardContent,
  Typography,
  useTheme,
  CardMedia,
  Grid,
  TableRow,
  TableCell,
  Box,
} from '@mui/material';

import { SvgColor } from '../svg-color';
import { Scrollbar } from '../scrollbar';
import { Strings } from '@app/constants';

interface SubCardProps {
  subTitle?: string;
  fields: string[];
  data: Array<string | number | JSX.Element | undefined>;
}

const SubCard = ({ subTitle, fields, data }: SubCardProps) => {
  const theme = useTheme();
  return (
    <Card sx={{ margin: 1 }}>
      {subTitle ? (
        <Stack
          sx={{
            borderRadius: '0px 0px 10px 10px',
            bgcolor: theme.palette.info.lighter,
            ml: '20px',
            mr: '120px',
          }}
        >
          <Typography
            fontWeight={600}
            fontSize={11}
            sx={{
              padding: '5px',
              textAlign: 'center',
            }}
          >
            {subTitle}
          </Typography>
        </Stack>
      ) : undefined}
      <CardContent>
        <Stack spacing={1}>
          {fields.map((field, index) => (
            <Stack direction="row" key={index} sx={{ alignItems: 'center', height: 35 }}>
              <TableRow key={index}>
                <TableCell
                  sx={{
                    fontWeight: 700,
                    borderBottom: 'none',
                    verticalAlign: 'top',
                    alignContent: 'center',
                    pr: '2px',
                    pl: '0px',
                    marginLeft: 6,
                  }}
                >
                  {field}:
                </TableCell>
                <TableCell sx={{ borderBottom: 'none', pr: ' 0px', pl: '12px' }}>
                  {data[index]}
                </TableCell>
              </TableRow>
            </Stack>
          ))}
        </Stack>
      </CardContent>
    </Card>
  );
};

interface MainCardProps {
  title?: string;
  subCards?: SubCardProps[];
  height?: string | number;
  iconImg?: any;
}

const InternalSubCard = ({ title, subCards, height, iconImg }: MainCardProps) => {
  const theme = useTheme();
  return (
    <Card sx={{ margin: 1, height }}>
      {title && (
        <Stack
          sx={{
            borderRadius: '0px 0px 10px 10px',
            bgcolor: theme.palette.info.lighter,
            ml: '60px',
            mr: '60px',
          }}
        >
          <Typography
            fontWeight={700}
            fontSize={13}
            sx={{
              padding: '5px',
              textAlign: 'center',
            }}
          >
            {title}
          </Typography>
        </Stack>
      )}
      <Scrollbar sx={{ height: '230px' }}>
        {iconImg && (
          <Grid item xs={12} md={12}>
            <CardMedia sx={{ display: 'flex', justifyContent: 'center', direction: 'row' }}>
              <Stack
                sx={{
                  width: 100,
                  height: 100,
                  mt: '20px',
                  border: '2px dashed #D8DBDE',
                  borderStyle: 'dashed',
                  borderRadius: '50%',
                }}
              >
                <Stack
                  sx={{
                    bgcolor: theme.palette.info.main,
                    width: 80,
                    height: 80,
                    borderRadius: '50%',
                    alignSelf: 'center',
                    mt: '10px',
                    mb: '10px',
                  }}
                >
                  <SvgColor
                    // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
                    src={`/assets/icons/card/${iconImg}.svg`}
                    sx={{
                      width: '50px',
                      height: '50px',
                      alignSelf: 'center',
                      color: theme.palette.common.white,
                      mt: '12px',
                    }}
                  />
                </Stack>
              </Stack>
            </CardMedia>
          </Grid>
        )}
        {subCards && subCards.length > 0 ? (
          subCards.map((subCard, index) => <SubCard key={index} {...subCard} />)
        ) : (
          <Box display="flex" justifyContent="center" mt={4}>
            <Typography variant="body2" color="textSecondary">
              {Strings.common.noRecord}
            </Typography>
          </Box>
        )}
      </Scrollbar>
    </Card>
  );
};

export { InternalSubCard };
